import React from "react";
import { Tabs, Tab, withStyles } from "@material-ui/core";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Routes } from "App";
import { useDispatch, useSelector } from "react-redux";
import { loadVisibleSections } from "redux/user/userFunctions";
import { RootState } from "redux/rootReducer";
import { VisibleSections } from "types/VisibleSections";
import { loadAssetClassParameters } from "redux/assetClassParameters/assetClassParametersFunctions";
import { getReportDateSelector } from "redux/user/user";

const CustomTabs = withStyles({
  root: {
    height: 30,
    minHeight: 30,
    marginTop: 2,
  },
  fixed: {
    height: 30,
  },
  indicator: {
    backgroundColor: "#FFFEF1",
  },
  scrollButtons: {
    color: "rgb(255, 255, 255)",
  },
})(Tabs);

const CustomTab = withStyles((theme) => ({
  root: {
    fontFamily: "Lato Regular",
    textTransform: "none",
    minWidth: 72,
    fontWeight: theme.typography.fontWeightRegular,
    color: "#003366",
    background:
      "linear-gradient(0deg, rgba(0, 0, 0, 0.1) 0%, rgba(255, 255, 255, 0) 16.67%), #DADADA",
    height: 27,
    minHeight: 27,
    opacity: 1,
    marginTop: 3,
    marginLeft: 0.3,
    marginRight: 0.3,
    "&:hover": {
      opacity: 1,
    },
    "&$selected": {
      fontWeight: "bold",
      height: 30,
      background: "#FFFFFF",
      marginTop: 0,
    },
    "&:focus": {},
  },
  selected: {},
}))((props: StyledTabProps) => <Tab disableRipple {...props} />);

interface StyledTabProps {
  label: string;
  value?: string;
}

const NavigationTabs = () => {
  const location = useLocation();
  const navigation = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleChange = (_event: React.ChangeEvent<{}>, newValue: string) => {
    navigation.push(newValue);
  };

  const reportControlId = useSelector((state: RootState) => state.user.reportControlId);
  const reportDate = useSelector(getReportDateSelector);

  React.useEffect(() => {
    if (reportControlId) {
      dispatch(loadVisibleSections(reportControlId));
      if (reportDate) {
        dispatch(loadAssetClassParameters(reportControlId, reportDate));
      }
    }
  }, [dispatch, reportControlId, reportDate]);

  const visibleSections = useSelector(
    (state: RootState) => state.user.visibleSections?.data as VisibleSections | undefined
  );
  const assetClassParamaters = useSelector((state: RootState) => state.assetClassParameters.data);

  const hasAssetClass = (assetClass: string) =>
    assetClassParamaters?.some((ap) => ap.AssetClass === assetClass) ?? false;

  const covenantsCount = useSelector((state: RootState) => state.covenants.count ?? 0);

  const showCovenants = covenantsCount > 1 && (visibleSections?.NaytaSitoumukset ?? false);

  return (
    <CustomTabs
      value={location.pathname}
      onChange={handleChange}
      variant="scrollable"
      scrollButtons="auto"
    >
      <CustomTab label={t("overview")} value={Routes.Overview} />
      {hasAssetClass("Art") && <CustomTab label={t("art")} value={Routes.Art} />}
      {hasAssetClass("Commodity") && (
        <CustomTab label={t("commodities")} value={Routes.Commodity} />
      )}
      {hasAssetClass("Crypto") && <CustomTab label={t("crypto")} value={Routes.Crypto} />}
      {hasAssetClass("Currency") && <CustomTab label={t("currency")} value={Routes.Currency} />}
      {hasAssetClass("Heino - Suorat sijoitukset") && (
        <CustomTab label={t("direct_investments")} value={Routes.DirectInvestments} />
      )}
      {hasAssetClass("Equity") && <CustomTab label={t("equity")} value={Routes.Equity} />}
      {hasAssetClass("Forest") && <CustomTab label={t("forest")} value={Routes.Forest} />}
      {hasAssetClass("Mixed Allocation") && (
        <CustomTab label={t("mixed_allocation")} value={Routes.MixedAllocation} />
      )}
      {hasAssetClass("Real Estate") && (
        <CustomTab label={t("real_estates")} value={Routes.RealEstate} />
      )}
      {hasAssetClass("Fixed Income") && (
        <CustomTab label={t("fixed_income")} value={Routes.FixedIncome} />
      )}
      {hasAssetClass("Derivative") && (
        <CustomTab label={t("derivatives")} value={Routes.Derivatives} />
      )}
      {hasAssetClass("Private Equity") && (
        <CustomTab label={t("private_equity")} value={Routes.PrivateEquity} />
      )}
      {hasAssetClass("Private Debt") && (
        <CustomTab label={t("private_debt")} value={Routes.PrivateDebt} />
      )}
      {hasAssetClass("Structured") && (
        <CustomTab label={t("structured")} value={Routes.Structured} />
      )}
      {hasAssetClass("Specialty") && <CustomTab label={t("specialty")} value={Routes.Specialty} />}
      {showCovenants && <CustomTab label={t("covenants")} value={Routes.Covenants} />}
      {visibleSections?.NaytaVoittoTappio && (
        <CustomTab label={t("profits_and_losses")} value={Routes.ProfitsAndLosses} />
      )}
      {visibleSections?.NaytaPaivakirja && <CustomTab label={t("logs")} value={Routes.Logs} />}
      {visibleSections?.NaytaSaldo && (
        <CustomTab label={t("statements")} value={Routes.Statements} />
      )}
    </CustomTabs>
  );
};

export default React.memo(NavigationTabs);
